import { Button } from '@/components/ui/button';
import { EquipmentTypeDisplay, NodeBody, NodeContainer, NodeErrorIcon, NodeHeader, NodePrompts } from '@/nodes/NodeUI';
import { appEquipmentConfigState, equipmentTypeState } from '@/state';
import { useAsyncAtomValue } from '@/state/loadableHooks';
import { type EquipmentTypeId, type GuideNodeId, outgoingHandles } from '@rock/types';
import { evaluateFieldDisplay, visibleCoreFields } from '@rock/util';
import { Handle, type NodeProps, Position } from '@xyflow/react';
import { type FC, useContext } from 'react';
import { FlowParamsContext } from '../app/FlowInitializer';
import type { EquipmentNodeType } from './types';

const EquipmentNode: FC<NodeProps<EquipmentNodeType>> = ({ id, selected, data }) => {
    const { spaceId } = useContext(FlowParamsContext);
    const paths = outgoingHandles.equipment(id as GuideNodeId, data);
    const appEquipmentConfig = useAsyncAtomValue(appEquipmentConfigState);
    const type = useAsyncAtomValue(
        equipmentTypeState(data.equipmentTypeId === '[context]' ? ('_' as EquipmentTypeId) : data.equipmentTypeId),
    );
    const scope = type?.scope ?? 'normal';
    const coreFields = visibleCoreFields(appEquipmentConfig?.coreFields ?? [], { scope, typeId: type?.id });
    const inputFields = type?.inputFields;
    const { shownFields, hiddenFields } = evaluateFieldDisplay(data.fieldDisplay, coreFields, inputFields);

    return (
        <NodeContainer>
            <NodeErrorIcon id={id} />
            <NodeHeader id={id} type={data.type} title={data.pageTitle} />
            <NodeBody selected={selected}>
                <NodePrompts prompts={data.prompts} />
                {data.equipmentTypeId && (
                    <div className="flex flex-col items-center justify-center p-1">
                        <EquipmentTypeDisplay spaceId={spaceId} typeId={data.equipmentTypeId} />
                    </div>
                )}
                <div className="flex flex-col items-stretch gap-1 px-5 my-3">
                    <div className="text-xs font-medium">Shown ({shownFields?.length || 0})</div>
                    {shownFields?.map((f) => (
                        <div key={f.dataKey} className="bg-white text-xs text-left p-2">
                            {f.label}
                        </div>
                    ))}
                    <div className="text-xs font-medium">Hidden Behind Dropdown ({hiddenFields?.length || 0})</div>
                    {hiddenFields?.map((f) => (
                        <div key={f.dataKey} className="bg-white text-xs text-left p-2">
                            {f.label}
                        </div>
                    ))}
                </div>
                <div className="flex flex-col items-center mb-2">
                    <Button size="sm">{data.buttonLabel || 'Button'}</Button>
                </div>
            </NodeBody>

            <Handle type="target" position={Position.Left} />
            <Handle type="source" id={paths.next} position={Position.Right} />
        </NodeContainer>
    );
};

export default EquipmentNode;
